//-- Copyright (c) 2021 TMT Insights Group, LLC
import styled from '@emotion/styled';
import { Box, Button, Grid, Typography, lighten } from '@mui/material';
import PolarisIcon from '@/components/_common/PolarisIcon';
import VersionsChip from '@/components/OrderPlacement/OPStep2FileSelection/TitleBrowser/VersionChip';
import React, { useMemo, useRef, useState } from 'react';
import { TitleEntity, VersionGroupedByParent } from '@/redux/slices/slice_types';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { setVersionSelections } from '@/redux/slices/orderPlacementSlice';
import useOnClickOutside from '@/components/_hooks/useOnClickOutside';
import { selectVersions } from '@/redux/selectors/orderPlacementSelectors';

type VersionSelectionPopoutProps = {
  data: VersionGroupedByParent;
};

const VersionSelectionPopout = ({ data }: VersionSelectionPopoutProps) => {
  const dispatch = useAppDispatch();
  const selectedVersions = useAppSelector(selectVersions);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const menuRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(menuRef, () => setIsOpen(false));
  const uniqueVersionsMap = useMemo(() => {
    const map = new Map<string, TitleEntity[]>();
    Object.values(data).map((value) => Object.values(value)).flat().forEach((version) => {
      if (map.has(version.name)) {
        map.get(version.name)?.push(version);
      } else {
        map.set(version.name, [version]);
      }
    });
    return map;
  }, [data]);

  const selectedVersionKeys = useMemo(() => {
    return selectedVersions.map((version) => version.id);
  }, [selectedVersions]);

  const handleButtonClick: React.MouseEventHandler<HTMLButtonElement> = (event) => {
    event.stopPropagation();
    setIsOpen(true);
  };

  const onVersionChipClick = (versions: TitleEntity[]) => {
    versions.forEach((version) => dispatch(setVersionSelections(version)));
  };

  return (
    <Wrapper>
      <Button onClick={handleButtonClick} disableRipple sx={{ mr: -2 }}>
        Add all <PolarisIcon icon="down_filled" style={{ marginLeft: 10 }} />
      </Button>
      <Menu open={isOpen} ref={menuRef}>
        <MenuContainer container>
          <Typography fontSize={11} mb={2} sx={{ opacity: 0.6 }}>
            Select Version(s)
          </Typography>
          <Grid container>
            {Array.from(uniqueVersionsMap.keys()).map((versionName) => {
              const versions = uniqueVersionsMap.get(versionName) || [];
              const versionIds = versions.map(version => version.id);
              return (
                <VersionsChip
                  key={versionName}
                  versionName={versionName}
                  versions={versions}
                  isSelected={selectedVersionKeys.some(id => versionIds.includes(id))}
                  onClick={() => onVersionChipClick(versions)}
                />
              );
            })}
          </Grid>
        </MenuContainer>
      </Menu>
    </Wrapper>
  );
};

const Wrapper = styled(Box)(() => ({
  position: 'relative',
  marginLeft: 'auto'
}));

const Menu = styled(Box)<{ open: boolean }>(({ theme, open }) => ({
  position: 'absolute',
  top: 40,
  right: -15,
  width: 280,
  borderRadius: 8,
  zIndex: 1,
  backgroundColor: lighten(theme.palette.background.paper, 0.04),
  boxShadow: '0 0 10 0 rgba(0, 0, 0, 0.3)',
  cursor: 'default',
  transform: open ? 'scale(1, 1)' : 'scale(0.5, 0)',
  transformOrigin: 'right top',
  transition: 'transform 0.15s ease',

  '&::before': {
    content: "''",
    position: 'absolute',
    right: 30,
    top: -5,
    borderBottom: `5px solid ${lighten(theme.palette.background.paper, 0.04)}`,
    borderLeft: '5px solid transparent',
    borderRight: '5px solid transparent'
  }
}));

const MenuContainer = styled(Grid)(() => ({
  padding: 16,
  '& .MuiGrid-root': {
    padding: 0
  }
}));

export default VersionSelectionPopout;
